import type { Absent, AbstractCtor, BaseClassOf, Json, Present, RecordKey } from './core.ts';
import { recordEntries, type MapRec } from './stronger_builtins.ts';

// TODO: Rename TypeAssertion for consistency
// export type TypeGuard<T extends V, V = unknown> = (v: V) => v is T;
export interface TypeGuard<T extends V, V = unknown> {
  (v: V): v is T;
  explain?: (v: V) => string;
}

export type TypeGuard2<A, T> = (v: unknown, arg: A) => v is T;

// TypeScript doesn't treat these as type guards, unfortunately.
// export type TypeGuard<T extends U, U = unknown> = (v: U) => v is T;
// export type TypeGuard2<A, T extends U, U = unknown> = (v: U, arg: A) => v is T;

// TODO: Instead of deprecating, move to subtle, as declare nullable values is occasionally useful.

/** @deprecated Prefer Present. Types that can be both undefined or null are confusing. */
export type Defined<T> = T extends undefined ? never : T; // Exclude<T, undefined>; // (T & {}) | (T & null);
// export type Defined<T> = T extends undefined ? never : T;

/** @deprecated Prefer Maybe. A priori, use undefined instead of null. */
export type NullOr<T> = T | null;

/** @deprecated Prefer Present. Types that can be both undefined or null are confusing. */
export type NonNull<T> = Exclude<T, null>; // (T & {}) | (T & undefined);

/** @deprecated Use isAbsent to conservatively handle both `undefined` and `null`. */
export function isUndefined(v: unknown): v is undefined {
  return v === undefined;
}

/** @deprecated Use isPresent to conservatively handle both `undefined` and `null`. */
export function isDefined<T>(v: T): v is Defined<T> {
  return v !== undefined;
}

/** @deprecated Use isAbsent to conservatively handle both `undefined` and `null`. */
export function isNull(v: unknown): v is null {
  return v === null;
}

/** @deprecated Use isPresent to conservatively handle both `undefined` and `null`. */
export function isNonNull<T>(v: T): v is NonNull<T> {
  return v !== null;
}

// Whether `v` is undefined or null.
// export function isAbsent<T>(v: T): v is Absent<T> {
export function isAbsent<T>(v: T): v is Absent<T> {
  // eslint-disable-next-line eqeqeq
  return v == null; // v === undefined || v === null;
}

// Whether `v` is not undefined nor null.
export function isPresent<T>(v: T): v is Present<T> {
  // eslint-disable-next-line eqeqeq
  return v != null;
}

export function typeGuard<T extends V, V = unknown>(
  test: (v: V) => boolean,
  explain?: (v: V) => string,
): TypeGuard<T, V>;
export function typeGuard<T extends V, V = unknown>(
  test: (v: V) => v is T,
  explain?: (v: V) => string,
): TypeGuard<T, V>;
export function typeGuard<T extends V, V = unknown>(
  test: ((v: V) => boolean) | ((v: V) => v is T),
  explain?: (v: V) => string,
): TypeGuard<T, V> {
  const result = test as (v: V) => v is T;
  return isPresent(explain) ? Object.assign(result, { explain }) : result;
}

// https://stackoverflow.com/q/58636396/8107589
// export function arePresent<T>(v: T[]): v is Present<T>[] {
//   // eslint-disable-next-line eqeqeq
//   return v.every((el) => el != null);
// }

export const AsGuard: unique symbol = Symbol('asGuard');

export function is<T>(expected: T): TypeGuard<T> {
  return <TypeGuard<T>>((v) => v === expected);
}

const isBoolean_ = typeGuard<boolean>(
  (v) => typeof v === 'boolean',
  (v) => `${String(v)} is not a boolean.`,
);

export function isBoolean(_: typeof AsGuard): TypeGuard<boolean>;
export function isBoolean(v: unknown): v is boolean;
export function isBoolean(v: unknown): TypeGuard<boolean> | boolean {
  return v === AsGuard ? isBoolean_ : isBoolean_(v);
}

const isNumber_ = <TypeGuard<number>>((v: unknown) => typeof v === 'number');

export function isNumber(_: typeof AsGuard): TypeGuard<number>;
export function isNumber(v: unknown): v is number;
export function isNumber(v: unknown): TypeGuard<number> | boolean {
  return v === AsGuard ? isNumber_ : isNumber_(v);
}

export function isString(v: unknown): v is string {
  return typeof v === 'string';
}

export function isSymbol(v: unknown): v is symbol {
  return typeof v === 'symbol';
}

export function isFunction(v: unknown): v is (...args: unknown[]) => unknown {
  return typeof v === 'function';
}

// https://stackoverflow.com/questions/56373756/typescript-constrain-generic-to-string-literal-type-for-use-in-computed-object-p
export type StringLiteral<T> = T extends string ? (string extends T ? never : T) : never;
export type NumericLiteral<T> = T extends number ? (number extends T ? never : T) : never;

// https://github.com/microsoft/TypeScript/issues/17002#issuecomment-494937708
// declare global {
//   interface ArrayConstructor {
//       isArray(arg: ReadonlyArray<any> | any): arg is ReadonlyArray<any>
//   }
// }
// https://github.com/microsoft/TypeScript/issues/17002#issuecomment-1295193597
// export function isMutableArray(v: unknown): v is [];
// export function isReadonlyArray(v: unknown): v is readonly [];
// export type IsMutableArray = (v: unknown) => v is [];
// export type IsReadonlyArray = (v: unknown) => v is readonly [];

// type IsArray<T> = T extends readonly [] ? IsReadonlyArray : IsMutableArray;
// type NarrowArray<T> = T extends readonly [] ? readonly [] : [];

// type Box<B> = { b: B };
// type Unbox<U> = U extends Box<unknown> ? U['b'] : never;
// type ExactType<T> = Unbox<{ b: T }>;

// // function foo<M>(v: M): M extends Box<unknown> ? M['w'] : never;
// // type ExactType<T> = ReturnType<typeof foo({ w: T })>>;
// // type ExactType<T> = ReturnType<typeof foo<{ v: T }>>; //  extends {}

// export function isArray<B extends Box<unknown>>(v: Unbox<B>): v is NarrowArray<Unbox<B>> {
//   return Array.isArray(v);
// }
// export const isArray: IsArray<T> = Array.isArray;

// export function isArray<T extends ReadonlyArray<unknown>>(v: T): v is T & ReadonlyArray<unknown>;
// export function isArray<T>(v: T): v is T & Array<unknown>;
//   v: T
// ): v is T extends ReadonlyArray<unknown> ? T & ReadonlyArray<unknown> : T & Array<unknown>;
// export function isArray(v: readonly unknown[]): v is ReadonlyArray<unknown>;
// export function isArray(v: unknown): v is Array<unknown>;
// export function isArray(v: unknown): v is Array<unknown> | ReadonlyArray<unknown> {
//   return Array.isArray(v);
// }
// export const isArray: TypeGuard<Array<unknown>>;
// export const isArray: TypeGuard<ReadonlyArray<unknown>>;
// export const isArray: TypeGuard<Array<unknown> | ReadonlyArray<unknown>> = Array.isArray;
export function isArrayish<T>(v: Array<T>): v is Array<T>;
export function isArrayish<T>(v: ReadonlyArray<T>): v is ReadonlyArray<T>;
export function isArrayish<T>(v: unknown): v is Array<T> | ReadonlyArray<T>;
export function isArrayish<T>(v: unknown): v is Array<T> | ReadonlyArray<T> {
  return Array.isArray(v);
}
// eslint-disable-next-line @typescript-eslint/naming-convention
export const isArray: typeof isArrayish = Array.isArray;

export function isArrayOf<T>(type: TypeGuard<T>): TypeGuard<Array<T>> {
  return <TypeGuard<Array<T>>>((v) => Array.isArray(v) && v.every((v) => type(v)));
}
export function isReadonlyArrayOf<T>(type: TypeGuard<T>): TypeGuard<ReadonlyArray<T>> {
  return <TypeGuard<ReadonlyArray<T>>>((v) => Array.isArray(v) && v.every((v) => type(v)));
}

type TypeGuardsOf<T extends unknown[]> = {
  [K in keyof T]: TypeGuard<T[K]>;
};
export function isTupleOf<T extends unknown[]>(...types: TypeGuardsOf<T>): TypeGuard<T> {
  return <TypeGuard<T>>((v) => Array.isArray(v) && v.every((t, i) => types[i](t)));
}

export function isIterable(v: unknown): v is Iterable<unknown> {
  // eslint-disable-next-line eqeqeq
  return v != null && typeof v === 'object' && Symbol.iterator in v;
}

export function isMemberOf<T extends string>(values: readonly T[]): TypeGuard<T>;
export function isMemberOf<T extends string>(values: readonly T[], v: unknown): v is T;
export function isMemberOf<T extends string>(
  values: readonly T[],
  v?: unknown,
): TypeGuard<T> | boolean {
  const test = typeGuard<T>(
    (v) => values.includes(v as T),
    (v) => `${String(v)} is not a member of [${values.map((v) => String(v)).join(',')}]`,
  );
  return isPresent(v) ? test(v) : test;
}

// Like `object`, except easier to access keys that may or may not exist.
// Note: TS 4.9 implicitly narrows the `object` type when testing literal keys (e.g. 'k' in obj),
// but not for variable keys (e.g. k in obj).
export type Objectish = { [P in RecordKey]?: unknown };

// I was hoping that any known type would implicitly cast to this, but nope.
// https://stackoverflow.com/questions/37006008/typescript-index-signature-is-missing-in-type
// export interface Recordish {
//   readonly [key: RecordKey]: unknown;
// }

export function isObject(v: unknown): v is Objectish {
  return typeof v === 'object' && v !== null;
}

export function isKeyOf<T extends object>(v: T): TypeGuard<keyof T & RecordKey>;
export function isKeyOf<T extends object>(obj: T, v: unknown): v is keyof T & RecordKey;
export function isKeyOf<T extends object>(
  obj: T,
  v?: unknown,
): TypeGuard<keyof T & RecordKey> | boolean {
  const test = <TypeGuard<keyof T & RecordKey>>((v: unknown) => (v as RecordKey) in obj);
  return isPresent(v) ? test(v) : test;
}

export function hasKey<T, K, U>(
  obj: T,
  k: StringLiteral<K>,
  guard: TypeGuard<U>,
): obj is T & { [P in StringLiteral<K>]: U } {
  return isObject(obj) && k in obj && guard((obj as MapRec<string, unknown>)[k]);
}

export function isSatisfying<T>(pred: (v: unknown) => boolean): TypeGuard<T> {
  return <TypeGuard<T>>pred;
}

// https://stackoverflow.com/a/54775885/8107589
export type CheckKeys<T, TExpected> =
  Exclude<keyof T, keyof TExpected> extends never
    ? // eslint-disable-next-line @typescript-eslint/no-empty-object-type
      {}
    : // eslint-disable-next-line @typescript-eslint/naming-convention
      { ExcessKeys: Exclude<keyof T, keyof TExpected> };

// Safer version of `instanceof` operator when the checked type is related to the target type.
// If the types truly are unrelated, then use `instanceof` directly, but we try to avoid that.
// You must also use instanceof when `Ctor` is not public.
/** @deprecated Prefer `v instanceof ctor` as the type inference is better. */
export function isInstance<T, C extends BaseClassOf<T>>(v: T, ctor: AbstractCtor<C>): v is T & C {
  return v instanceof ctor;
}

// This predicate wouldn't be necessary if lambdas could be inferred as type guards.
// e.g. (x) => isInstance(x, Foo)
// https://github.com/microsoft/TypeScript/issues/5101#issuecomment-145693151
export function isInstanceOf<T, C extends BaseClassOf<T>>(
  ctor: AbstractCtor<C>,
  predicate?: (v: C) => boolean,
): (v: T) => v is C & T {
  return <(v: T) => v is C & T>((v) => v instanceof ctor && (predicate?.(v) ?? true));
}

export function isRecord<T>(v: unknown, type: TypeGuard<T>): v is Record<RecordKey, T> {
  return isObject(v) && Object.values(v).every((v) => type(v));
}

export function isRecordOf<T>(type: TypeGuard<T>): TypeGuard<Record<RecordKey, T>> {
  return <TypeGuard<Record<RecordKey, T>>>((v) => isRecord<T>(v, type));
}

export function isJson(v: unknown, maxDepth = 3): v is Json {
  if (v === null) {
    return true;
  }
  switch (typeof v) {
    case 'string':
    case 'number':
    case 'boolean':
      return true;
  }
  if (maxDepth > 1) {
    if (isArray(v)) {
      return v.every((e) => isJson(e, maxDepth - 1));
    }
    if (isObject(v)) {
      return Object.entries(v).every(([k, v]) => isString(k) && isJson(v, maxDepth - 1));
    }
  }
  return false;
}

type PropGuards<R> =
  R extends Record<infer K, unknown>
    ? {
        [P in K]: TypeGuard<R[K]>;
      }
    : never;

export function hasShape<T>(props: PropGuards<T>): (v: unknown) => v is T {
  return <(v: unknown) => v is T>(
    ((v) => isObject(v) && recordEntries(props).every(([k, g]) => g(v[k])))
  );
}

// https://github.com/microsoft/TypeScript/issues/27808
// https://stackoverflow.com/a/71012039/8107589
// type Possibly<V, T> = [V] extends [T] ? unknown : V;
// type Possibly<V, T> = (V & T) extends never ? V : unknown;

export function validate2<V, T>(v: V, guard: (v: unknown) => v is T) {
  return guard(v) ? v : undefined;
}

export function validate<T extends V, V = unknown>(v: V, guard: TypeGuard<T, V>): T | undefined;
export function validate<T extends V, O, V = unknown>(
  v: V,
  guard: TypeGuard<T, V>,
  otherwise: O,
): T | O;
export function validate<T extends V, O, V = unknown>(
  v: V,
  guard: TypeGuard<T, V>,
  otherwise?: O,
): T | O | undefined {
  return guard(v) ? v : otherwise;
}
